import api from './api';

/**
 * Service for booking-related API calls
 */

/**
 * Create a new booking
 * @param {Object} bookingData - Data for the new booking
 * @returns {Promise<Object>} Created booking data
 */
export const createBooking = async (bookingData) => {
  try {
    // Format dates if they are Date objects
    const formattedData = {
      ...bookingData,
      checkInDate: bookingData.checkInDate instanceof Date 
        ? bookingData.checkInDate.toISOString() 
        : bookingData.checkInDate,
      checkOutDate: bookingData.checkOutDate instanceof Date 
        ? bookingData.checkOutDate.toISOString() 
        : bookingData.checkOutDate,
      pickupDateTime: bookingData.pickupDateTime instanceof Date 
        ? bookingData.pickupDateTime.toISOString() 
        : bookingData.pickupDateTime,
      panditServiceDateTime: bookingData.panditServiceDateTime instanceof Date 
        ? bookingData.panditServiceDateTime.toISOString() 
        : bookingData.panditServiceDateTime,
      ePoojaDateTime: bookingData.ePoojaDateTime instanceof Date 
        ? bookingData.ePoojaDateTime.toISOString() 
        : bookingData.ePoojaDateTime,
    };

    const response = await api.post('/bookings', formattedData);
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

/**
 * Fetch all bookings
 * @param {Object} filters - Optional filters for bookings
 * @returns {Promise<Array>} Array of booking data
 */
export const fetchAllBookings = async (filters = {}) => {
  try {
    const response = await api.get('/bookings', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};

/**
 * Fetch a booking by ID
 * @param {string} bookingId - The ID of the booking to fetch
 * @returns {Promise<Object>} Booking data
 */
export const fetchBookingById = async (bookingId) => {
  try {
    const response = await api.get(`/bookings/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Fetch a booking by reference number
 * @param {string} referenceNumber - The reference number of the booking
 * @returns {Promise<Object>} Booking data
 */
export const fetchBookingByReferenceNumber = async (referenceNumber) => {
  try {
    const response = await api.get(`/bookings/reference/${referenceNumber}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching booking with reference ${referenceNumber}:`, error);
    throw error;
  }
};

/**
 * Update a booking
 * @param {string} bookingId - The ID of the booking to update
 * @param {Object} updateData - Data to update the booking with
 * @returns {Promise<Object>} Updated booking data
 */
export const updateBooking = async (bookingId, updateData) => {
  try {
    // Format dates if they are Date objects
    const formattedData = {
      ...updateData,
      checkInDate: updateData.checkInDate instanceof Date 
        ? updateData.checkInDate.toISOString() 
        : updateData.checkInDate,
      checkOutDate: updateData.checkOutDate instanceof Date 
        ? updateData.checkOutDate.toISOString() 
        : updateData.checkOutDate,
      pickupDateTime: updateData.pickupDateTime instanceof Date 
        ? updateData.pickupDateTime.toISOString() 
        : updateData.pickupDateTime,
      panditServiceDateTime: updateData.panditServiceDateTime instanceof Date 
        ? updateData.panditServiceDateTime.toISOString() 
        : updateData.panditServiceDateTime,
      ePoojaDateTime: updateData.ePoojaDateTime instanceof Date 
        ? updateData.ePoojaDateTime.toISOString() 
        : updateData.ePoojaDateTime,
    };

    const response = await api.patch(`/bookings/${bookingId}`, formattedData);
    return response.data;
  } catch (error) {
    console.error(`Error updating booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Update booking status
 * @param {string} bookingId - The ID of the booking to update
 * @param {string} status - New status for the booking
 * @returns {Promise<Object>} Updated booking data
 */
export const updateBookingStatus = async (bookingId, status) => {
  try {
    const response = await api.patch(`/bookings/${bookingId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error(`Error updating booking ${bookingId} status:`, error);
    throw error;
  }
};

/**
 * Apply a coupon to a booking
 * @param {string} bookingId - The ID of the booking
 * @param {string} couponCode - The coupon code to apply
 * @returns {Promise<Object>} Updated booking data with applied coupon
 */
export const applyBookingCoupon = async (bookingId, couponCode) => {
  try {
    const response = await api.patch(`/bookings/${bookingId}/apply-coupon`, { couponCode });
    return response.data;
  } catch (error) {
    console.error(`Error applying coupon to booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Remove a coupon from a booking
 * @param {string} bookingId - The ID of the booking
 * @returns {Promise<Object>} Updated booking data with coupon removed
 */
export const removeBookingCoupon = async (bookingId) => {
  try {
    const response = await api.patch(`/bookings/${bookingId}/remove-coupon`);
    return response.data;
  } catch (error) {
    console.error(`Error removing coupon from booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Update payment information for a booking
 * @param {string} bookingId - The ID of the booking
 * @param {Object} paymentData - Payment data to update
 * @returns {Promise<Object>} Updated booking data
 */
export const updateBookingPayment = async (bookingId, paymentData) => {
  try {
    const response = await api.patch(`/bookings/${bookingId}/payment`, paymentData);
    return response.data;
  } catch (error) {
    console.error(`Error updating payment for booking ${bookingId}:`, error);
    throw error;
  }
};

/**
 * Delete a booking
 * @param {string} bookingId - The ID of the booking to delete
 * @returns {Promise<void>}
 */
export const deleteBooking = async (bookingId) => {
  try {
    await api.delete(`/bookings/${bookingId}`);
  } catch (error) {
    console.error(`Error deleting booking ${bookingId}:`, error);
    throw error;
  }
};
